import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { t } from '../i18n';
import './LocationPicker.css';

type LocationPickerProps = {
  longitude: string;
  latitude: string;
  onChange: (next: { longitude: string; latitude: string }) => void;
  mapHeight?: number;
  extraActions?: React.ReactNode;
  readOnly?: boolean;
};

const DEFAULT_CENTER = {
  lat: 40.4168,
  lng: -3.7038
};

const MAP_LIBRARIES: ('places')[] = ['places'];

const formatCoord = (value: number) => value.toFixed(6);

const toNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  longitude,
  latitude,
  onChange,
  mapHeight = 320,
  extraActions,
  readOnly = false
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'xperiences-google-maps-script',
    googleMapsApiKey: apiKey,
    libraries: MAP_LIBRARIES
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [geoError, setGeoError] = useState('');

  const markerPosition = useMemo(() => {
    const lng = toNumber(longitude);
    const lat = toNumber(latitude);

    if (lng === null || lat === null) return null;

    return { lat, lng };
  }, [latitude, longitude]);

  const center = markerPosition || DEFAULT_CENTER;

  useEffect(() => {
    if (!mapRef.current || !markerPosition) return;

    mapRef.current.panTo(markerPosition);
    mapRef.current.setZoom(14);
  }, [markerPosition]);

  const setCoordinates = (next: { lat: number; lng: number }) => {
    onChange({
      longitude: formatCoord(next.lng),
      latitude: formatCoord(next.lat)
    });

    if (mapRef.current) {
      mapRef.current.panTo({ lat: next.lat, lng: next.lng });
    }
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    const location = place?.geometry?.location;
    if (!location) return;

    setSearchValue(place.formatted_address || place.name || '');
    setGeoError('');

    setCoordinates({
      lat: location.lat(),
      lng: location.lng()
    });
  };

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;

    setGeoError('');

    setCoordinates({
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    });
  };

  const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;

    setGeoError('');

    setCoordinates({
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    });
  };

  const clearCoordinates = () => {
    onChange({ longitude: '', latitude: '' });
    setSearchValue('');
    setGeoError('');
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError(t('locationPicker.geoNotSupported'));
      return;
    }

    setIsLocating(true);
    setGeoError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoordinates({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsLocating(false);
      },
      () => {
        setGeoError(t('locationPicker.geoError'));
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000
      }
    );
  };

  if (!apiKey) {
    return (
      <div className="location-picker-notice">
        <strong>{t('locationPicker.noApiKey')}</strong>
        <span>{t('locationPicker.noApiKeyHelp')}</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="location-picker-notice location-picker-notice-error">
        <strong>{t('locationPicker.loadError', 'Could not load Google Maps')}</strong>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="location-picker">
        {isLoaded ? (
          <div className="location-picker-map-wrap">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: `${mapHeight}px` }}
              onLoad={(map) => {
                mapRef.current = map;
              }}
              center={center}
              zoom={markerPosition ? 14 : 5}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false
              }}
            >
              {markerPosition && (
                <MarkerF position={markerPosition} />
              )}
            </GoogleMap>
          </div>
        ) : (
          <div className="location-picker-loading">{t('locationPicker.loadingMap')}</div>
        )}
      </div>
    );
  }

  return (
    <div className="location-picker">
      <label className="location-picker-label">{t('locationPicker.searchLabel')}</label>

      {isLoaded ? (
        <Autocomplete
          onLoad={(instance) => {
            autocompleteRef.current = instance;
          }}
          onPlaceChanged={handlePlaceChanged}
        >
          <div className="location-picker-clear-wrap">
            <input
              type="text"
              className="location-picker-search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={t('locationPicker.searchPlaceholder')}
            />
            {searchValue && (
              <button
                type="button"
                className="location-picker-field-clear"
                onClick={() => setSearchValue('')}
                aria-label={t('common.clearSearch')}
                title={t('common.clearSearch')}
              >
                x
              </button>
            )}
          </div>
        </Autocomplete>
      ) : (
        <div className="location-picker-loading">{t('locationPicker.loadingMap')}</div>
      )}

      <div className="location-picker-actions">
        <button
          type="button"
          className="location-picker-action-btn"
          onClick={useCurrentLocation}
          disabled={isLocating}
        >
          {isLocating ? t('locationPicker.locating') : t('locationPicker.useCurrent')}
        </button>
        <button type="button" className="location-picker-clear" onClick={clearCoordinates}>
          {t('locationPicker.clear')}
        </button>
        {extraActions}
      </div>

      <div className="location-picker-grid">
        <div className="location-picker-input-wrap">
          <label className="location-picker-label">{t('locationPicker.longitude')}</label>
          <div className="location-picker-clear-wrap">
            <input
              className="location-picker-input"
              type="number"
              step="any"
              value={longitude}
              onChange={(event) => {
                setGeoError('');
                onChange({ longitude: event.target.value, latitude });
              }}
              placeholder="-3.703790"
            />
            {longitude && (
              <button
                type="button"
                className="location-picker-field-clear"
                onClick={() => {
                  setGeoError('');
                  onChange({ longitude: '', latitude });
                }}
                aria-label={t('common.clearSearch')}
                title={t('common.clearSearch')}
              >
                x
              </button>
            )}
          </div>
        </div>

        <div className="location-picker-input-wrap">
          <label className="location-picker-label">{t('locationPicker.latitude')}</label>
          <div className="location-picker-clear-wrap">
            <input
              className="location-picker-input"
              type="number"
              step="any"
              value={latitude}
              onChange={(event) => {
                setGeoError('');
                onChange({ longitude, latitude: event.target.value });
              }}
              placeholder="40.416780"
            />
            {latitude && (
              <button
                type="button"
                className="location-picker-field-clear"
                onClick={() => {
                  setGeoError('');
                  onChange({ longitude, latitude: '' });
                }}
                aria-label={t('common.clearSearch')}
                title={t('common.clearSearch')}
              >
                x
              </button>
            )}
          </div>
        </div>
      </div>

      {geoError && <p className="location-picker-error">{geoError}</p>}
      {searchValue && <p className="location-picker-selected">{t('locationPicker.selectedAddress')}: {searchValue}</p>}

      {isLoaded && (
        <div className="location-picker-map-wrap">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: `${mapHeight}px` }}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            center={center}
            zoom={markerPosition ? 14 : 5}
            onClick={handleMapClick}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false
            }}
          >
            {markerPosition && (
              <MarkerF
                position={markerPosition}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>
        </div>
      )}

      <p className="location-picker-hint">{t('locationPicker.hint')}</p>
    </div>
  );
};

export default LocationPicker;
